import { Controller } from "@hotwired/stimulus";

const OPT_IN_CHECKBOX_IDS = [
  "opt_in_to_communications",
  "opt_in_to_email",
  "opt_in_to_marketing_sms",
  "opt_in_to_account_notification_sms",
];

const OPT_IN_DANGER_MESSAGES = {
  opt_in_to_account_notification_sms: {
    preserveOff:
      "Toggling this on will opt these contacts in globally to receive account notification SMS messages (updates the per-phone subscription record). Only do this if you have documented proof of opt-in consent.",
    preserveOn:
      "Toggling this on will mark these contacts as opted in to account notification SMS for this contact list — audiences using this list will treat them as eligible. Existing global SMS opt-in records aren't changed. Only do this if you have documented proof of opt-in consent.",
  },
  opt_in_to_marketing_sms: {
    preserveOff:
      "Toggling this on will opt these contacts in globally to receive marketing SMS messages (updates the per-phone subscription record). Only do this if you have documented proof of opt-in consent.",
    preserveOn:
      "Toggling this on will mark these contacts as opted in to marketing SMS for this contact list — audiences using this list will treat them as eligible. Existing global SMS opt-in records aren't changed. Only do this if you have documented proof of opt-in consent.",
  },
};

const OFF_MESSAGE_NO_PRESERVE_FIELD = `Since this is toggled off, everyone will be opted out, including users who are currently opted in.`;
const OFF_MESSAGE_PRESERVE_AVAILABLE =
  `Since this is toggled off, everyone will be opted out, including users who are currently opted in. ` +
  `Toggle "Preserve current global opt-in status" on to keep current opt-ins.`;
const OFF_MESSAGE_PRESERVE_ON =
  `Since this is toggled off, contacts uploaded will be marked opted out for this contact list, ` +
  `so audiences built from this list won't reach them. ` +
  `Existing global SMS and email opt-in records won't be changed.`;

export default class extends Controller {
  static targets = ["optInAlertTemplate", "optInDangerAlertTemplate"];

  static values = {
    subscriptionStatusConfirmationTexts: Object,
  };

  connect() {
    this.#setupOptInAlerts();
    this.#setupSelectize();
  }

  disconnect() {
    if (this.statusSelector) {
      this.statusSelector.off("change", this.setMessage);
    }
    if (this.optInCheckboxes) {
      this.optInCheckboxes.forEach(({ checkbox, handler }) => {
        checkbox.removeEventListener("change", handler);
      });
    }
  }

  setMessage = (status) => {
    const message = this.subscriptionStatusConfirmationTextsValue[status];
    this.confirmationMessage.innerHTML = message;
    this.confirmationTextField.value = message;
  };

  #setupOptInAlerts() {
    if (!this.hasOptInAlertTemplateTarget) return;

    this.optInCheckboxes = [];
    this.preserveCheckbox = this.element.querySelector("#preserve_current_opt_in_status");

    OPT_IN_CHECKBOX_IDS.forEach((id) => {
      const checkbox = this.element.querySelector(`#${id}`);
      if (!checkbox) return;

      const fieldContainer = checkbox.closest(".field-unit__field");

      const alert = this.optInAlertTemplateTarget.content.cloneNode(true).firstElementChild;
      const alertTitle = alert.querySelector("h5");
      alert.hidden = true;
      fieldContainer.appendChild(alert);

      const dangerMessages = OPT_IN_DANGER_MESSAGES[id];
      let dangerAlert = null;
      let dangerTitle = null;

      if (dangerMessages && this.hasOptInDangerAlertTemplateTarget) {
        dangerAlert = this.optInDangerAlertTemplateTarget.content.cloneNode(true).firstElementChild;
        dangerTitle = dangerAlert.querySelector("h5");
        dangerAlert.hidden = true;
        fieldContainer.appendChild(dangerAlert);
      }

      const updateAlert = () => {
        const preserveOn = this.preserveCheckbox && this.preserveCheckbox.checked;
        if (!this.preserveCheckbox) {
          alertTitle.textContent = OFF_MESSAGE_NO_PRESERVE_FIELD;
        } else if (preserveOn) {
          alertTitle.textContent = OFF_MESSAGE_PRESERVE_ON;
        } else {
          alertTitle.textContent = OFF_MESSAGE_PRESERVE_AVAILABLE;
        }
        alert.hidden = checkbox.checked;
        if (dangerAlert) {
          dangerTitle.textContent = preserveOn
            ? dangerMessages.preserveOn
            : dangerMessages.preserveOff;
          dangerAlert.hidden = !checkbox.checked;
        }
      };

      updateAlert();
      checkbox.addEventListener("change", updateAlert);
      this.optInCheckboxes.push({ checkbox, handler: updateAlert });

      if (this.preserveCheckbox) {
        this.preserveCheckbox.addEventListener("change", updateAlert);
        this.optInCheckboxes.push({ checkbox: this.preserveCheckbox, handler: updateAlert });
      }
    });
  }

  #setupSelectize() {
    const nativeSelector = document.querySelector("#contact_upload_batch_sms_double_opt_in_status");
    if (!nativeSelector) return;

    this.#waitForSelectize(nativeSelector).then((selectize) => {
      this.statusSelector = selectize;
      this.confirmationTextField = document.querySelector(
        "#contact_upload_batch_subscription_status_confirmation_text"
      );

      this.confirmationMessage = document.createElement("div");
      this.confirmationMessage.classList.add("text-sm", "text-gray-500", "italic");
      document
        .querySelector("input[name='contact_upload_batch[subscription_status_confirmation]']")
        .parentElement.appendChild(this.confirmationMessage);

      selectize.on("change", this.setMessage);
      this.setMessage(selectize.getValue());
    });
  }

  #waitForSelectize(nativeSelector) {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (nativeSelector.selectize) {
          clearInterval(interval);
          resolve(nativeSelector.selectize);
        }
      }, 100);
    });
  }
}
